import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { Snackbar, Alert } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Helmet } from 'react-helmet-async';
import { enGB } from 'date-fns/locale';
import axios from './plugins/axios';
import { removeSnack } from './store/slices/snackSlice';
import { setServerValues } from './store/slices/configSlice';
import Routes from './routes/Routes';
import LandingPage from './pages/LandingPage';
import TokenCheckerDialog from './components/TokenCheckerDialog';
import maTheme from './theme';
import history from './routes/history';
import reactQueryClient from './utils/reactQueryClient';
import { useAuth } from './contexts/authContext';

const apiUrl = import.meta.env.VITE_WORKNEST_TOOLS_API_URL as string;

const App = () => {
  const dispatch = useAppDispatch();
  const snackState = useAppSelector(state => state.snackReducer);
  const { user } = useAuth();

  useEffect(() => {
    async function initialiseUserSession() {
      try {
        const response = await axios.get(`${apiUrl}/vars`);
        dispatch(setServerValues(response.data));

        const redirect = localStorage.getItem('redirect');
        if (redirect != null) {
          history.push(redirect);
          localStorage.removeItem('redirect');
        }
      } catch (error) {
        console.error(error);
      }
    }
    initialiseUserSession();
  }, [dispatch]);

  const dismissSnack = () => dispatch(removeSnack());

  return (
    <>
      <Helmet titleTemplate="%s | Worknest Tools" defaultTitle="WorkNest Tools" />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <MuiThemeProvider theme={maTheme[0]}>
          <StyledEngineProvider injectFirst>
            <QueryClientProvider client={reactQueryClient}>
              {user ? <Routes /> : <LandingPage />}
              <TokenCheckerDialog />
              <ReactQueryDevtools />
            </QueryClientProvider>
          </StyledEngineProvider>
        </MuiThemeProvider>
      </LocalizationProvider>
      <Snackbar
        style={{
          height: snackState.snack.severity === 'warning' ? '100%' : 'auto',
        }}
        open={snackState.open}
        onClose={dismissSnack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={dismissSnack} severity={snackState.snack.severity}>
          {snackState.snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default App;
